import React, { useEffect, useState } from "react";
import { formatSubjectName } from "../../../layouts/schoolPortal/schoolPortalTheme";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Menu, MenuItem,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import moment from "moment";
import { useSnackbar } from "notistack";
import {
  reschedule_booking_api, mark_no_show_api, get_my_slots_for_reschedule_api,
  instructor_cancel_booking_api, get_recording_consent_api, post_recording_consent_api,
  get_instructor_cancellation_quote_api,
} from "../../../DAL/Tutors/Tutors";
import {
  border, textPrimary, textSecondary, textMuted, font,
  success, warning, error as errorColor,
  inputSx, primaryButtonSx, secondaryButtonSx, dialogPaperSx,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * The three things you can do to a booking that had no button anywhere.
 *
 * Reschedule, cancel and no-show were all built server-side and reachable by
 * nobody. Cancel had a client but only for the assigned instructor, so with a
 * CFI off sick the front desk could refund a session that had already happened
 * and not cancel one that had not — and that phone call comes to the front desk.
 *
 * WHY ONE COMPONENT RATHER THAN THREE BUTTONS PER SCREEN
 * They are the same decision from the same row: something has changed about a
 * session that is in the diary. Splitting them across surfaces is how "cancel"
 * ended up on the instructor's screen and nowhere else for a year.
 *
 * RESCHEDULE IS NOT CANCEL-AND-REBOOK, and the endpoint's own doc comment
 * explains why at length: cancelling releases the slot, voids the authorization
 * and rebooking takes a new slot and a new hold — between those two steps the
 * student can lose the time to someone else and a session both people agreed
 * to move simply evaporates. Moving the same row keeps the money and the
 * identity, and the .ics keeps its UID and updates rather than cancelling.
 *
 * NO-SHOW CAN MOVE MONEY. A student no-show keeps the card hold, so the
 * session is charged and the instructor paid as normal. An instructor no-show
 * is only recorded here; the student's refund goes through the admin refund.
 */

/**
 * RESCHEDULE IS OFF.
 *
 * The endpoint, the slot picker and the dialog all exist, but the flow is not
 * finished — and a half-built reschedule is worse than none, because it moves a
 * session, a payment hold and a calendar invite in one press. Off in one place
 * rather than deleted: turning it back on is this constant, and the code it
 * gates is still here to be finished.
 */
const RESCHEDULE_ENABLED = false;

const label = { fontFamily: font, fontSize: "10px", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: textSecondary };

/** Free windows from the CALLER's own diary — see the empty state for why. */
function SlotPicker({ duration, subject, chosen, onChoose, canTeach }) {
  const [state, setState] = useState({ loading: true, slots: [], error: null, timezone: null });

  useEffect(() => {
    let cancelled = false;
    get_my_slots_for_reschedule_api({ duration, subject }).then((r) => {
      if (cancelled) return;
      if (r.code === 200) setState({ loading: false, slots: r.slots || [], error: null, timezone: r.timezone || null });
      else setState({ loading: false, slots: [], error: r.message || "Could not load your free times", timezone: null });
    });
    return () => { cancelled = true; };
  }, [duration, subject]);

  const byDay = state.slots.reduce((acc, s) => {
    const d = moment(s.start_time).format("ddd D MMM");
    (acc[d] = acc[d] || []).push(s);
    return acc;
  }, {});

  if (state.loading) return <CircularProgress size={16} sx={{ color: textMuted }} />;
  if (state.error) return <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: errorColor }}>{state.error}</Typography>;

  if (!state.slots.length) {
    return (
      <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, lineHeight: 1.5 }}>
        {canTeach
          ? "You have no free slots in the next three weeks. Set your working hours under My Availability, or clear a block."
          // The endpoint builds slots from the CALLER's working hours, so a
          // manager who does not teach gets an empty list. Telling them to fix
          // their availability is advice for a problem they do not have.
          : "These are times from your own diary, and you are not set up to teach. Ask the instructor to move it, or cancel it instead."}
      </Typography>
    );
  }

  return (
    <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
      {state.timezone && (
        <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mb: 0.5 }}>
          Times in {state.timezone}
        </Typography>
      )}
      {Object.entries(byDay).map(([day, list]) => (
        <Box key={day} sx={{ mb: 1 }}>
          <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: textSecondary, mb: 0.4 }}>{day}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {list.map((s) => {
              const on = chosen?.start_time === s.start_time;
              return (
                <Button
                  key={s.start_time}
                  onClick={() => onChoose(s)}
                  sx={{
                    minHeight: 0, py: 0.5, px: 1.2, fontSize: "12px", fontFamily: font, fontWeight: 600,
                    textTransform: "none", borderRadius: "7px",
                    border: `1px solid ${on ? success : border}`,
                    backgroundColor: on ? `${success}14` : "transparent",
                    color: on ? success : textPrimary,
                  }}
                >{moment(s.start_time).format("HH:mm")}</Button>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Recording consent.
 *
 * The statement is rendered from the SERVER's text, never written here. Consent
 * is to a specific wording, and a client that paraphrases collects agreement to
 * something nobody said — which is why the GET returns the statement at all.
 *
 * A consent given against older wording is not consent to the current one: the
 * service treats that as `stale` and refuses to record, and this says so rather
 * than showing a tick.
 */
function ConsentPanel({ bookingId, onDone }) {
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    get_recording_consent_api(bookingId).then((r) => {
      if (cancelled) return;
      if (r.code === 200) setState({ loading: false, data: r, error: null });
      else setState({ loading: false, data: null, error: r.message || "Could not load the consent state" });
    });
    return () => { cancelled = true; };
  }, [bookingId]);

  const answer = async (granted) => {
    setBusy(true);
    try {
      const r = await post_recording_consent_api(bookingId, granted);
      enqueueSnackbar(r.message || "Recorded", { variant: r.code === 200 ? "success" : "error" });
      if (r.code === 200) {
        const fresh = await get_recording_consent_api(bookingId);
        if (fresh.code === 200) setState({ loading: false, data: fresh, error: null });
        onDone?.();
      }
    } finally { setBusy(false); }
  };

  if (state.loading) return <CircularProgress size={16} sx={{ color: textMuted }} />;
  if (state.error) return <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: errorColor }}>{state.error}</Typography>;

  const d = state.data;
  const why = {
    all_consented: "Both of you have agreed — this session will be recorded.",
    someone_declined: "Someone declined, so it will not be recorded. The session goes ahead as normal.",
    wording_changed: "The wording has changed since it was agreed, so it has to be agreed again.",
    not_answered: "Waiting on an answer before recording can start.",
  }[d.reason] || "";

  return (
    <>
      <Typography sx={{
        fontFamily: font, fontSize: "12.5px", color: textPrimary, lineHeight: 1.6,
        border: `1px solid ${border}`, borderRadius: "8px", p: 1.25, mb: 1.25,
        whiteSpace: "pre-wrap",
      }}>
        {d.statement?.text}
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: "12px", color: d.allowed ? success : textSecondary, mb: 1.25 }}>
        {why}
      </Typography>
      {d.yourAnswerNeeded ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={() => answer(true)} disabled={busy} sx={{ ...primaryButtonSx, flex: 1 }}>
            {busy ? "Saving…" : "I agree"}
          </Button>
          <Button onClick={() => answer(false)} disabled={busy} sx={secondaryButtonSx}>No thanks</Button>
        </Box>
      ) : (
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted, lineHeight: 1.45 }}>
          You have already answered. You can change your mind — answering again replaces it, and a withdrawal
          is kept on the record.
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button onClick={() => answer(true)} disabled={busy} sx={{ ...secondaryButtonSx, flex: 1 }}>Agree</Button>
            <Button onClick={() => answer(false)} disabled={busy} sx={secondaryButtonSx}>Withdraw</Button>
          </Box>
        </Typography>
      )}
    </>
  );
}

// `triggerLabel`, not `label`: there is already a module-level `label` style
// object in this file, and a prop of that name shadows it inside the component
// — which would have spread the string into the sx of the dialog's own
// headings and quietly unstyled them.
export default function BookingActions({ booking, canTeach = true, isManager = false, onDone, triggerLabel = null, inline = false }) {
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState(null);
  const [mode, setMode] = useState(null);        // reschedule | cancel | no_show
  const [chosen, setChosen] = useState(null);
  const [reason, setReason] = useState("");
  const [who, setWho] = useState("student");
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);

  // The money consequence, fetched when the dialog opens rather than described
  // in fixed copy. services/tutorsRefundPolicy.js is the only thing that knows
  // the answer, and it depends on the school's notice window and how close the
  // session is — neither of which a hardcoded sentence can track. Declared up
  // here with the rest: below the `if (!booking)` return it is a conditional
  // hook, and `booking?._id` keeps the dependency legal when there is none.
  useEffect(() => {
    if (mode !== "cancel" || !booking?._id) { setQuote(null); return undefined; }
    let cancelled = false;
    get_instructor_cancellation_quote_api(booking._id)
      .then((r) => { if (!cancelled) setQuote(r?.code === 200 ? r.data : null); })
      .catch(() => { if (!cancelled) setQuote(null); });
    return () => { cancelled = true; };
  }, [mode, booking?._id]);

  if (!booking) return null;
  const started = new Date(booking.start_time) <= new Date();
  const open = ["confirmed", "pending_instructor_approval", "pending_student_confirmation"].includes(booking.status);

  const close = () => { setMode(null); setChosen(null); setReason(""); setQuote(null); };

  const run = async () => {
    setBusy(true);
    try {
      let r;
      if (mode === "reschedule") {
        if (!chosen) { enqueueSnackbar("Pick a new time first", { variant: "warning" }); return; }
        r = await reschedule_booking_api(booking._id, {
          start_time: chosen.start_time, end_time: chosen.end_time, reason,
        });
      } else if (mode === "cancel") {
        r = await instructor_cancel_booking_api(booking._id);
      } else {
        r = await mark_no_show_api(booking._id, who, reason);
      }
      enqueueSnackbar(r.message || "Done", { variant: r.code === 200 ? "success" : "error" });
      if (r.code === 200) { close(); onDone?.(); }
    } finally { setBusy(false); }
  };

  /* Shared by both shapes below: the menu and the inline buttons open
     the same dialogs, so there is one implementation of cancelling.
     Declared before the `inline` return so both can render it. */
  const dialog = (
    <Dialog open={Boolean(mode)} onClose={close} PaperProps={{ sx: dialogPaperSx }} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontFamily: font, fontSize: "15px", fontWeight: 800 }}>
        {mode === "reschedule" && "Reschedule this session"}
        {mode === "cancel" && "Cancel this session"}
        {mode === "no_show" && "Mark a no-show"}
        {mode === "consent" && "Recording this session"}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography sx={{ ...label, mb: 0.5 }}>Currently</Typography>
        <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 700, color: textPrimary, mb: 1.75 }}>
          {moment(booking.start_time).format("ddd D MMM, HH:mm")}
          {booking.subject ? ` · ${formatSubjectName(booking.subject)}` : ""}
        </Typography>
  
        {mode === "reschedule" && (
          <>
            {/* Said before the picker, not after. Somebody about to move a
                session is deciding whether to do that or cancel and rebook,
                and this is the answer to that question. */}
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mb: 1.25, lineHeight: 1.5 }}>
              Moving it keeps the payment and the calendar invite — the invite updates
              rather than cancelling. Cancelling and rebooking loses both, and the slot can go to
              somebody else in between.
            </Typography>
            <Typography sx={{ ...label, mb: 0.5 }}>Move it to</Typography>
            <SlotPicker
              duration={moment(booking.end_time).diff(moment(booking.start_time), "minutes") || 60}
              subject={booking.subject || ""}
              chosen={chosen}
              onChoose={setChosen}
              canTeach={canTeach}
            />
            <TextField
              size="small" fullWidth multiline minRows={2} label="Why (they will see this)"
              value={reason} onChange={(e) => setReason(e.target.value)} sx={{ ...inputSx, mt: 1.5 }}
              placeholder="Weather looks bad Thursday — moving to Saturday."
            />
            {new Date(booking.start_time) - Date.now() < 24 * 3600 * 1000 && (
              <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: warning, mt: 1, lineHeight: 1.45 }}>
                This is inside 24 hours. Moving it is allowed and recorded — who bears the cost of a late
                change is your school's policy, not something the platform decides.
              </Typography>
            )}
          </>
        )}
  
        {mode === "cancel" && quote && (
          <Box sx={{
            border: `1px solid ${border}`, borderRadius: "10px", overflow: "hidden", mb: 1.5,
          }}>
            <Box sx={{ backgroundColor: "#F5F2EF", px: 1.5, py: 0.85, borderBottom: `1px solid ${border}` }}>
              <Typography sx={{ ...label, m: 0 }}>What this costs</Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 1.1 }}>
              {[
                ["Student gets back", quote.refund, quote.refund.pence > 0 ? success : textPrimary],
                ["You keep", quote.instructorKeeps, quote.instructorKeeps.pence > 0 ? textPrimary : errorColor],
              ].map(([k, v, colour]) => (
                <Box key={k} sx={{ display: "flex", justifyContent: "space-between", py: 0.3 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary }}>{k}</Typography>
                  <Typography sx={{
                    fontFamily: font, fontSize: "12.5px", fontWeight: 800, color: colour,
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    {new Intl.NumberFormat("en-GB", { style: "currency", currency: v.currency }).format(v.amount)}
                  </Typography>
                </Box>
              ))}
              <Typography sx={{
                fontFamily: font, fontSize: "11.5px", color: textSecondary,
                mt: 0.85, pt: 0.85, borderTop: `1px solid ${border}`, lineHeight: 1.5,
              }}>
                {quote.reason}
              </Typography>
              {quote.paymentSettled && (
                <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mt: 0.5, lineHeight: 1.5 }}>
                  Their card was already charged, so this is a refund and can take a few days to show.
                </Typography>
              )}
              {!quote.paymentSettled && quote.refund.pence > 0 && (
                <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mt: 0.5, lineHeight: 1.5 }}>
                  Nothing was ever taken from their card — the hold is simply released.
                </Typography>
              )}
            </Box>
          </Box>
        )}
  
        {mode === "cancel" && (
          <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary, lineHeight: 1.55 }}>
            The student is told and refunded in full.
            {isManager && (
              <Box component="span" sx={{ display: "block", color: textSecondary, fontSize: "12px", mt: 1 }}>
                You are cancelling on the instructor's behalf. The student is refunded in full and the
                slot goes back into the instructor's diary.
              </Box>
            )}
          </Typography>
        )}
  
        {mode === "consent" && <ConsentPanel bookingId={booking._id} onDone={onDone} />}
  
        {mode === "no_show" && (
          <>
            <TextField
              select size="small" fullWidth label="Who did not turn up" value={who}
              onChange={(e) => setWho(e.target.value)} sx={{ ...inputSx, mb: 1.5 }}
            >
              <MenuItem value="student">The student</MenuItem>
              <MenuItem value="instructor">The instructor</MenuItem>
            </TextField>
            {/* The consequence, stated, because the two options are not
                symmetrical and picking the wrong one moves money. */}
            <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: who === "instructor" ? warning : textSecondary, mb: 1.5, lineHeight: 1.5 }}>
              {who === "instructor"
                ? "Recorded. Refund the student from the booking's refund option. They did nothing wrong and should not pay for it."
                : "Recorded. The session is not refundable, so it is charged and the instructor is paid as normal."}
            </Typography>
            <TextField
              size="small" fullWidth multiline minRows={2} label="Note"
              value={reason} onChange={(e) => setReason(e.target.value)} sx={inputSx}
              placeholder="Waited 20 minutes, no message."
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} sx={secondaryButtonSx}>{mode === "consent" ? "Close" : "Back"}</Button>
        {mode !== "consent" && <Button
          onClick={run} disabled={busy || (mode === "reschedule" && !chosen)}
          sx={{ ...primaryButtonSx, ...(mode === "cancel" ? { backgroundColor: errorColor } : {}) }}
        >
          {busy ? "Working…" : mode === "reschedule" ? "Reschedule it" : mode === "cancel" ? "Cancel the session" : "Record it"}
        </Button>}
      </DialogActions>
    </Dialog>
  );

  /**
   * NAMED BUTTONS, NOT AN OVERFLOW.
   *
   * On a session's own page there is room to say what you can do, and an
   * instructor looking for "cancel" behind an unlabelled dot reasonably
   * concluded there was no way to cancel their own lesson. Cancel carries the
   * destructive colour and nothing else does, so the one action that moves
   * money never looks like the ones that do not.
   */
  if (inline) {
    const canCancel = booking.status === "confirmed" && !started;
    const canNoShow = booking.status === "confirmed" && started;
    if (!canCancel && !canNoShow) return null;
    return (
      <>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {canCancel && (
            <Button
              size="small"
              onClick={() => setMode("cancel")}
              sx={{
                textTransform: "none", fontFamily: font, fontSize: "12.5px", fontWeight: 700,
                borderRadius: "999px", px: 1.5, py: 0.4, minHeight: 0,
                color: errorColor, backgroundColor: "#fff", border: `1px solid ${errorColor}44`,
                "&:hover": { backgroundColor: `${errorColor}0d`, borderColor: errorColor },
              }}
            >
              Cancel this session
            </Button>
          )}
          {canNoShow && (
            <Button
              size="small"
              onClick={() => setMode("no_show")}
              sx={{
                textTransform: "none", fontFamily: font, fontSize: "12.5px", fontWeight: 700,
                borderRadius: "999px", px: 1.5, py: 0.4, minHeight: 0,
                color: warning, backgroundColor: "#fff", border: `1px solid ${warning}44`,
                "&:hover": { backgroundColor: `${warning}0d`, borderColor: warning },
              }}
            >
              Mark a no-show
            </Button>
          )}
        </Box>
        {dialog}
      </>
    );
  }


  return (
    <>
      {/* A BARE ⋯ IS NOT AN AFFORDANCE WHEN ITS NEIGHBOURS ARE WORDS.
          In a dense row of sessions the icon is right: it is the overflow, and
          the primary action beside it is the point. On the session page it sat
          next to buttons reading "Hand over" and "Change topic", so the only
          way to find out you could cancel was to press an unlabelled dot. An
          instructor looking for cancel there reported, correctly, that there
          was no way to cancel. `triggerLabel` opts that surface into a named
          button;
          everywhere else keeps the icon. */}
      {triggerLabel ? (
        <Button
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          startIcon={<MoreHorizIcon sx={{ fontSize: 15 }} />}
          sx={{
            textTransform: "none", fontFamily: font, fontSize: "12.5px", fontWeight: 600,
            color: textPrimary, border: `1px solid ${border}`, borderRadius: "999px",
            px: 1.25, py: 0.3, minHeight: 0, backgroundColor: "#fff",
            "&:hover": { borderColor: errorColor, color: errorColor },
          }}
        >
          {triggerLabel}
        </Button>
      ) : (
        <Button
          size="small" onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ minWidth: 0, p: 0.4, color: textMuted }}
        ><MoreHorizIcon sx={{ fontSize: 18 }} /></Button>
      )}
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {/* Only what is actually possible for this booking right now. A menu
            offering an action the server will refuse is worse than no menu. */}
        {RESCHEDULE_ENABLED && open && !started && (
          <MenuItem onClick={() => { setAnchor(null); setMode("reschedule"); }} sx={{ fontFamily: font, fontSize: "13px" }}>
            Reschedule
          </MenuItem>
        )}
        {booking.status === "confirmed" && !started && (
          <MenuItem onClick={() => { setAnchor(null); setMode("cancel"); }} sx={{ fontFamily: font, fontSize: "13px", color: errorColor }}>
            Cancel this session
          </MenuItem>
        )}
        {booking.status === "confirmed" && started && (
          <MenuItem onClick={() => { setAnchor(null); setMode("no_show"); }} sx={{ fontFamily: font, fontSize: "13px" }}>
            Mark a no-show
          </MenuItem>
        )}
        {open && (
          <MenuItem onClick={() => { setAnchor(null); setMode("consent"); }} sx={{ fontFamily: font, fontSize: "13px" }}>
            Recording consent
          </MenuItem>
        )}
        {!open && (
          <MenuItem disabled sx={{ fontFamily: font, fontSize: "13px" }}>
            Nothing to do — this session is {booking.status}
          </MenuItem>
        )}
      </Menu>

      {dialog}
    </>
  );
}
